import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
   Menu, X, User, LogIn, LogOut, Settings, LayoutDashboard, MessageCircle,
   Heart, Globe, Package, Tag, Search, Plus, Shield, Truck, ChevronDown,
   Sparkles, Calculator, BookOpen, GitCompare, Phone, ShoppingBag, Bell,
   CreditCard, Mail
} from "lucide-react";
import ComprarMegaMenu from "@/components/layout/ComprarMegaMenu";
import NotificationBell from "@/components/notifications/NotificationBell";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
 import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
const langLabels = { es: "🇪🇸 ES", ca: "🇦🇩 CA", en: "🇬🇧 EN" } as const;

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [scrolled, setScrolled] = useState(false);
    const { user, profile, isAdmin, isModerator, isSuperAdmin, signOut, session } = useAuth();
   const [mfaStatus, setMfaStatus] = useState<"not_verified" | "verified" | "no_factors">("verified");
 
   useEffect(() => {
     const checkMfa = async () => {
       if (!user || !isSuperAdmin) return;
       
       const { supabase } = await import("@/integrations/supabase/client");
       const { data: factors } = await supabase.auth.mfa.listFactors();
       const hasMfa = factors?.all?.some(f => f.status === 'verified');
       const isVerified = session?.user?.factors?.some(f => f.status === 'verified');
       
       if (hasMfa && !isVerified) setMfaStatus("not_verified");
       else if (!hasMfa) setMfaStatus("no_factors");
       else setMfaStatus("verified");
     };
     checkMfa();
   }, [user, session, isSuperAdmin]);
  const { locale, setLocale, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const unreadCount = useUnreadMessages();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setMobileOpen(false);
    navigate("/");
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/buscar${searchValue ? `?q=${encodeURIComponent(searchValue)}` : ""}`);
    setMobileOpen(false);
  };

    const showAdminLink = isAdmin || isModerator || isSuperAdmin;
  const isActive = (path: string) => location.pathname === path;
  const initials = (profile?.first_name?.[0] || user?.email?.[0] || "U").toUpperCase();

  const mainLinks = [
    { to: "/buscar", label: t("nav.explore"), icon: Search },
    { to: "/blog", label: t("nav.blog"), icon: BookOpen },
  ];

  const toolItems = [
    { to: "/comparador", label: t("nav.comparator"), icon: GitCompare, desc: "Compara modelos lado a lado" },
    { to: "/guia-compra", label: t("nav.guide"), icon: BookOpen, desc: "Todo lo que debes saber" },
    { to: "/calculadora", label: t("nav.calculator"), icon: Calculator, desc: "Calcula tu ahorro mensual" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top utility bar */}
      <div className={cn(
        "hidden border-b border-border/40 bg-muted/30 transition-all md:block",
        scrolled && "h-0 overflow-hidden border-none opacity-0"
      )}>
        <div className="container flex h-9 items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-primary" /> Patinetes homologados DGT</span>
            <span className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-primary" /> Envío a toda España</span>
            <span className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" /> Publica gratis tu primer anuncio</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/contacto" className="flex items-center gap-1 transition-colors hover:text-foreground">
              <Phone className="h-3 w-3" /> Contacto
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 transition-colors hover:text-foreground">
                <Globe className="h-3 w-3" /> {langLabels[locale]}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLocale("es")}>🇪🇸 Español</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLocale("ca")}>🇦🇩 Català</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLocale("en")}>🇬🇧 English</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className={cn(
        "w-full transition-all duration-300",
        scrolled
          ? "border-b border-border/60 bg-background/85 backdrop-blur-xl shadow-sm"
          : "border-b border-transparent bg-background/60 backdrop-blur-md"
      )}>
        <nav className="container flex h-16 items-center justify-between gap-2 md:h-20 lg:h-24 lg:gap-4">
        {/* Logo */}
          <Link to="/" className="flex shrink min-w-0 flex-1 items-center gap-1.5 group sm:gap-2 overflow-hidden" aria-label="Volver al inicio">
            <BrandLogo size="lg" className="transition-transform group-hover:-translate-y-0.5" />
          </Link>

        {/* Desktop nav links */}
        <div className="ml-2 hidden items-center gap-1 lg:flex">
          {/* Comprar mega-dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-foreground/70 transition-all hover:bg-muted/60 hover:text-foreground data-[state=open]:bg-muted/60 data-[state=open]:text-foreground">
              <ShoppingBag className="h-4 w-4" />
              Comprar
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="p-0" sideOffset={8}>
              <ComprarMegaMenu />
            </DropdownMenuContent>
          </DropdownMenu>

          {mainLinks.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                "relative flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all",
                isActive(to)
                  ? "text-primary"
                  : "text-foreground/70 hover:text-foreground hover:bg-muted/60"
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
              {isActive(to) && (
                <span className="absolute -bottom-0.5 left-3 right-3 h-0.5 rounded-full bg-primary" />
              )}
            </Link>
          ))}

          {/* Tools mega-dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-foreground/70 transition-all hover:bg-muted/60 hover:text-foreground data-[state=open]:bg-muted/60 data-[state=open]:text-foreground">
              <Sparkles className="h-4 w-4" />
              Herramientas
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-2">
              {toolItems.map(({ to, label, icon: Icon, desc }) => (
                <DropdownMenuItem
                  key={to}
                  onClick={() => navigate(to)}
                  className="cursor-pointer gap-3 rounded-md p-3"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">{label}</span>
                    <span className="text-xs text-muted-foreground">{desc}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Search bar (desktop) */}
        <form onSubmit={handleSearch} className="ml-auto hidden max-w-sm flex-1 lg:flex">
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Buscar marca, modelo, ciudad..."
              className="h-10 rounded-full border-border/60 bg-muted/40 pl-10 pr-4 text-sm focus-visible:bg-background focus-visible:ring-1 focus-visible:ring-primary/40"
            />
          </div>
        </form>

        {/* Right side actions */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          {/* Sell CTA - always visible */}
          <Button
            asChild
            size="sm"
            className="h-9 shrink-0 gap-1 rounded-full bg-gradient-to-r from-primary to-primary/85 px-2.5 text-xs font-semibold shadow-md shadow-primary/20 transition-all hover:shadow-lg hover:shadow-primary/30 sm:h-10 sm:gap-1.5 sm:px-4 sm:text-sm"
          >
            <Link to="/publicar" aria-label="Publicar anuncio">
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> <span className="hidden xs:inline">Anunciar</span>
            </Link>
          </Button>

          {user ? (
            <div className="hidden items-center gap-0.5 md:flex">
              <NotificationBell />
              <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full" onClick={() => navigate("/favoritos")} title={t("nav.favorites")}>
                <Heart className="h-[18px] w-[18px]" />
              </Button>
              <Button variant="ghost" size="icon" className="relative h-10 w-10 rounded-full" onClick={() => navigate("/mensajes")} title={t("nav.messages")}>
                <MessageCircle className="h-[18px] w-[18px]" />
                {unreadCount > 0 && (
                  <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground ring-2 ring-background">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="ml-1 h-10 gap-2 rounded-full pl-1 pr-3">
                     <Avatar className="h-9 w-9 border border-border transition-transform group-hover:scale-105">
                       <AvatarImage src={profile?.avatar_url} alt={profile?.nickname || profile?.first_name || ""} />
                      <AvatarFallback className="bg-gradient-to-br from-primary/20 to-accent/20 text-xs font-semibold text-primary">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden max-w-[100px] truncate text-sm font-medium xl:block">
                      {profile?.first_name || user.email?.split("@")[0]}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-semibold">{profile?.first_name || "Mi cuenta"}</p>
                    <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/perfil")}>
                    <Settings className="mr-2 h-4 w-4" /> {t("nav.profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mis-anuncios")}>
                    <LayoutDashboard className="mr-2 h-4 w-4" /> {t("nav.myListings")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/favoritos")}>
                    <Heart className="mr-2 h-4 w-4" /> {t("nav.favorites")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mis-pedidos")}>
                    <Package className="mr-2 h-4 w-4" /> Mis pedidos
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mis-ventas")}>
                    <Tag className="mr-2 h-4 w-4" /> Mis ventas
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mis-alertas")}>
                    <Bell className="mr-2 h-4 w-4" /> Mis alertas
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mensajes")}>
                    <MessageCircle className="mr-2 h-4 w-4" /> {t("nav.messages")}
                  </DropdownMenuItem>
                   {showAdminLink && (
                     <>
                       <DropdownMenuSeparator />
                       <DropdownMenuItem asChild className={cn(isSuperAdmin && mfaStatus === "not_verified" && "text-amber-600 font-medium")}>
                         <Link to="/admin" className="flex w-full items-center">
                           <Shield className="mr-2 h-4 w-4" /> 
                           {t("nav.admin")} {isSuperAdmin && mfaStatus === "not_verified" && "(MFA Requerido)"}
                         </Link>
                       </DropdownMenuItem>
                       
                        {isSuperAdmin && (
                          <>
                            <DropdownMenuItem asChild className={cn(mfaStatus === "not_verified" && "text-amber-600 font-medium")}>
                              <Link to="/admin?section=settings_general" className="flex w-full items-center">
                                <Settings className="mr-2 h-4 w-4" /> Configuración Portal
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild className="pl-8 text-xs text-muted-foreground">
                              <Link to="/admin?section=shipping" className="flex w-full items-center">
                                <Truck className="mr-2 h-3 w-3" /> Envíos
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild className="pl-8 text-xs text-muted-foreground">
                              <Link to="/admin?section=payments" className="flex w-full items-center">
                                <CreditCard className="mr-2 h-3 w-3" /> Pagos
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild className="pl-8 text-xs text-muted-foreground">
                              <Link to="/admin?section=newsletter" className="flex w-full items-center">
                                <Mail className="mr-2 h-3 w-3" /> Newsletter
                              </Link>
                            </DropdownMenuItem>
                          </>
                        )}
                     </>
                   )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="hidden items-center gap-1 md:flex">
              <Button variant="ghost" size="sm" asChild className="h-10 rounded-full">
                <Link to="/login"><LogIn className="mr-1.5 h-4 w-4" /> {t("nav.login")}</Link>
              </Button>
            </div>
          )}

          {/* Mobile trigger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-10 w-10 shrink-0 rounded-full lg:hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                aria-label="Abrir menú"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm p-0">
              <SheetHeader className="border-b border-border p-4">
                <SheetTitle className="flex items-center gap-2 text-left">
                  <BrandLogo size="md" showTag />
                </SheetTitle>
              </SheetHeader>

              <div className="flex flex-col gap-4 overflow-y-auto p-4">
                {/* Mobile search */}
                <form onSubmit={handleSearch} className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Buscar..."
                    className="h-11 rounded-full bg-muted/50 pl-10"
                  />
                </form>

                {/* Sell CTA */}
                <Button asChild size="lg" className="w-full gap-2 rounded-full bg-gradient-to-r from-primary to-primary/85 font-semibold" onClick={() => setMobileOpen(false)}>
                  <Link to="/publicar"><Plus className="h-4 w-4" /> Publicar anuncio</Link>
                </Button>

                {/* Comprar (mobile) */}
                <div className="flex flex-col gap-2">
                  <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Comprar</p>
                  <div className="flex flex-wrap gap-1.5 px-1">
                    {[
                      { label: "Ocasión", to: "/buscar?condicion=usado" },
                      { label: "Nuevos", to: "/buscar?condicion=nuevo" },
                      { label: "Outlet", to: "/buscar?outlet=1" },
                      { label: "Homologados DGT", to: "/buscar?dgt=1" },
                      { label: "Recambios", to: "/buscar?categoria=recambios" },
                      { label: "Accesorios", to: "/buscar?categoria=accesorios" },
                    ].map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setMobileOpen(false)}
                        className="rounded-full border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Main links */}
                <div className="flex flex-col gap-0.5">
                  <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Navegar</p>
                  {[
                    { to: "/buscar", label: t("nav.explore"), icon: Search },
                    { to: "/blog", label: t("nav.blog"), icon: BookOpen },
                    { to: "/comparador", label: t("nav.comparator"), icon: GitCompare },
                    { to: "/guia-compra", label: t("nav.guide"), icon: BookOpen },
                    { to: "/calculadora", label: t("nav.calculator"), icon: Calculator },
                    { to: "/contacto", label: "Contacto", icon: Phone },
                  ].map(({ to, label, icon: Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                        isActive(to) ? "bg-primary/10 text-primary" : "hover:bg-muted"
                      )}
                    >
                      <Icon className="h-4 w-4" /> {label}
                    </Link>
                  ))}
                </div>

                {/* User area */}
                <div className="border-t border-border pt-3">
                  {user ? (
                    <div className="flex flex-col gap-0.5">
                      <div className="mb-2 flex items-center gap-3 rounded-lg bg-muted/40 p-3">
                       <Avatar className="h-10 w-10 border border-border">
                         <AvatarImage src={profile?.avatar_url} alt={profile?.nickname || profile?.first_name || ""} />
                          <AvatarFallback className="bg-gradient-to-br from-primary/20 to-accent/20 text-sm font-semibold text-primary">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate text-sm font-semibold">{profile?.first_name || "Mi cuenta"}</span>
                          <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                        </div>
                      </div>
                      {[
                        { to: "/perfil", label: t("nav.profile"), icon: Settings },
                        { to: "/mis-anuncios", label: t("nav.myListings"), icon: LayoutDashboard },
                        { to: "/favoritos", label: t("nav.favorites"), icon: Heart },
                        { to: "/mis-pedidos", label: "Mis pedidos", icon: Package },
                        { to: "/mis-ventas", label: "Mis ventas", icon: Tag },
                        { to: "/mis-alertas", label: "Mis alertas", icon: Bell },
                        { to: "/mensajes", label: t("nav.messages"), icon: MessageCircle },
                        ...(showAdminLink ? [{ to: "/admin", label: t("nav.admin"), icon: Shield }] : []),
                         ...(isSuperAdmin ? [{ to: "/admin?section=settings_general", label: "Configuración Portal", icon: Settings }] : []),
                      ].map(({ to, label, icon: Icon }) => (
                        <Link
                          key={to}
                          to={to}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-muted"
                        >
                          <Icon className="h-4 w-4 text-muted-foreground" /> {label}
                        </Link>
                      ))}
                      <button
                        onClick={handleSignOut}
                        className="mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-destructive hover:bg-destructive/10"
                      >
                        <LogOut className="h-4 w-4" /> {t("nav.logout")}
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Button asChild variant="outline" className="w-full rounded-full" onClick={() => setMobileOpen(false)}>
                        <Link to="/login"><LogIn className="mr-2 h-4 w-4" /> {t("nav.login")}</Link>
                      </Button>
                      <Button asChild className="w-full rounded-full" onClick={() => setMobileOpen(false)}>
                        <Link to="/registro"><User className="mr-2 h-4 w-4" /> {t("nav.register")}</Link>
                      </Button>
                    </div>
                  )}
                </div>

                {/* Language */}
                <div className="border-t border-border pt-3">
                  <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Idioma</p>
                  <div className="flex gap-2 px-3">
                    {(["es", "ca", "en"] as const).map((l) => (
                      <button
                        key={l}
                        onClick={() => setLocale(l)}
                        className={cn(
                          "flex-1 rounded-full border px-3 py-2 text-xs font-medium transition-colors",
                          locale === l ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"
                        )}
                      >
                        {langLabels[l]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      </div>
    </header>
  );
};

export default Navbar;
